<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Name Input -->
              <base-input
                col="3"
                type="number"
                :placeholder="$t('PLACEHOLDERS.orderNum')"
                v-model.trim="filterOptions.orderNum"
              />
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.clientName')"
                v-model="filterOptions.clientName"
              />
              <base-input
                col="3"
                type="text"
                :placeholder="$t('Orders.providerName')"
                v-model.trim="filterOptions.storeName"
              />
              <base-select-input
                col="3"
                :optionsList="activeStatuses"
                :placeholder="$t('PLACEHOLDERS.orderStatus')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Input -->
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="isWaitingRequest"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("Orders.cancelledOrders") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- Start:: No Data State -->

        <template v-slot:[`item.serial_number`]="{ item, index }">
          <p class="blue-grey--text text--darken-1 fs-3" v-if="!item.id">-</p>
          <p v-else>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>

        <template v-slot:[`item.id`]="{ item }">
          <h6 class="text-danger" v-if="!item.id">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.id }}</h6>
        </template>

        <!-- Start:: Activation Status -->
        <template v-slot:[`item.cancel_status`]="{ item }">
          <v-chip
            class="bg-primary text-white"
            v-if="item.cancel_status_enum === 'new'"
          >
            {{ item.cancel_status }}
          </v-chip>
          <v-chip
            class="bg-success text-white"
            v-if="item.cancel_status_enum === 'accepted'"
          >
            {{ item.cancel_status }}
          </v-chip>
          <v-chip
            class="bg-danger text-white"
            v-if="item.cancel_status_enum === 'rejected'"
          >
            {{ item.cancel_status }}
          </v-chip>
        </template>
        <!-- End:: Activation Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- // Show Icon -->
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye fs-5"></i>
              </button>
            </a-tooltip>

            <template
              v-if="$can('orders acceptorrejectcancellation', 'orders')"
            >
              <a-tooltip
                placement="bottom"
                v-if="item.cancel_status_enum === 'new'"
              >
                <template slot="title">
                  <span>{{ $t("STATUS.accepted") }}</span>
                </template>
                <button class="btn_activate" @click="selectAcceptItem(item)">
                  <i class="fad fa-check-circle fs-5"></i>
                </button>
              </a-tooltip>

              <a-tooltip
                placement="bottom"
                v-if="item.cancel_status_enum === 'new'"
              >
                <template slot="title">
                  <span>{{ $t("STATUS.canceled") }}</span>
                </template>
                <button
                  class="btn_deactivate"
                  @click="HandlingItemActivationStatus(item)"
                >
                  <i class="fad fa-times-circle fs-5"></i>
                </button>
              </a-tooltip>
            </template>

            <!-- // Date Change Icon -->
            <div v-if="item.status_enum === 'new'">
              <template>
                <a-tooltip placement="bottom">
                  <template slot="title">
                    <span>{{ $t("STATUS.changeDate") }}</span>
                  </template>
                  <button
                    class="btn_deactivate"
                    @click="selectDateChangeItem(item)"
                  >
                    <i class="fad fa-clock fs-5"></i>
                  </button>
                </a-tooltip>
              </template>
            </div>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Accept Modal -->
          <v-dialog v-model="dialogAccept">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToAccept">
                {{
                  $t("Orders.acceptDialog", {
                    name: itemToAccept.salon.name,
                  })
                }}
              </v-card-title>

              <form class="w-100 row">
                <base-input
                  col="6"
                  type="number"
                  :placeholder="$t('Orders.provider_cancel_discount')"
                  v-model="provider_cancel_discount"
                />
                <base-input
                  col="6"
                  type="number"
                  :placeholder="$t('Orders.client_cancel_composition')"
                  v-model="client_cancel_composition"
                />
              </form>

              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmAcceptItem">
                  {{ $t("BUTTONS.ok") }}
                </v-btn>

                <v-btn
                  class="modal_cancel_btn"
                  @click="
                    (dialogAccept = false),
                      (provider_cancel_discount = null),
                      (client_cancel_composition = null)
                  "
                  >{{ $t("BUTTONS.cancel") }}</v-btn
                >
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Deactivate Modal -->

          <v-dialog v-model="dialogDateChange">
            <v-card>
              <v-card-title
                class="text-h5 justify-center"
                v-if="itemToChangeDate"
              >
                {{
                  $t("Orders.changeDateMessage", {
                    name: itemToChangeDate.client_name,
                  })
                }}
              </v-card-title>

              <form class="w-100 row">
                <!-- <span>{{ orderDate }}</span> -->
                <div class="wrapper d-flex flex-wrap justify-content-center">
                  <v-chip class="bg-success text-white px-6 mt-1">
                    {{
                      $t("Orders.date", {
                        date: orderDate,
                      })
                    }}
                  </v-chip>
                  <hr class="border-0 w-100 m-2" />
                  <v-chip
                    class="bg-gray-300 p-2 mb-3 w-50 text-center justify-content-center"
                    v-if="salonType == 'freelancer'"
                  >
                    {{
                      $t("Orders.startTime", {
                        time: orderHour,
                      })
                    }}
                    <!-- {{ orderHour || $t("Orders.noTimesAvailable") }} -->
                  </v-chip>
                  <!-- <hr style="border: none" /> -->
                </div>

                <base-picker-input
                  col="12"
                  type="date"
                  :disabledDate="disabledDate"
                  :placeholder="$t('Orders.newDate')"
                  v-model="newDate"
                />

                <div
                  class="col-12 d-flex flex-wrap justify-content-center text-center gap-4"
                >
                  <!-- :: Date & Times -->
                  <div
                    class="col-12 d-flex flex-wrap justify-content-center"
                    v-if="newDate"
                  >
                    <div
                      v-if="workingDays.length > 0"
                      class="d-flex flex-wrap justify-content-center"
                    >
                      <div v-for="time in workingDays" :key="time.id">
                        <v-chip class="timeChip p-6 m-1">
                          {{ $t("Orders.workingTime", { time: time.value }) }}
                        </v-chip>
                      </div>
                    </div>
                    <div v-else>
                      <v-chip class="timeChip p-6 m-1">
                        {{ $t("Orders.noTimesAvailable") }}
                      </v-chip>
                    </div>
                  </div>

                  <!-- :: Services and Packages in one row -->
                  <div
                    v-if="salonType == 'salon'"
                    class="d-flex flex-wrap justify-content-center text-center gap-4 w-100"
                  >
                    <!-- Services -->
                    <div
                      class="d-flex flex-row flex-wrap justify-content-center"
                      v-if="
                        services.length > 0 && workingDays.length > 0 && newDate
                      "
                    >
                      <div
                        v-for="service in services"
                        :key="service.id"
                        class="d-flex flex-column align-items-center justify-content-center m-3 p-4 border rounded"
                        style="min-width: 200px"
                      >
                        <v-chip
                          class="bg-gray-300 p-2 mb-2 w-100 text-center justify-content-center"
                        >
                          {{ service.name }}
                        </v-chip>
                        <v-chip
                          class="bg-gray-300 p-2 mb-3 w-100 text-center justify-content-center"
                        >
                          {{ service.time || $t("Orders.noTimesAvailable") }}
                        </v-chip>
                        <v-chip
                          class="bg-gray-300 p-2 mb-3 w-100 text-center justify-content-center"
                        >
                          {{
                            $t("Orders.serviceDurationDialog", {
                              time: service.duration,
                            })
                          }}
                        </v-chip>
                        <base-picker-input
                          class="w-100"
                          type="time"
                          :placeholder="$t('Orders.newTime')"
                          v-model="service.newTime"
                          :allowed-times="allowedTimes"
                        />
                      </div>
                    </div>

                    <!-- Packages -->
                    <div
                      class="d-flex flex-row flex-wrap justify-content-center"
                      v-if="
                        packages.length > 0 && workingDays.length > 0 && newDate
                      "
                    >
                      <div
                        v-for="pack in packages"
                        :key="pack.id"
                        class="d-flex flex-column align-items-center justify-content-center m-3 p-4 border rounded"
                        style="min-width: 200px"
                      >
                        <v-chip
                          class="bg-gray-300 p-2 mb-2 w-100 text-center justify-content-center"
                        >
                          {{ pack.name }}
                        </v-chip>
                        <v-chip
                          class="bg-gray-300 p-2 mb-3 w-100 text-center justify-content-center"
                        >
                          {{ pack.time || $t("Orders.noTimesAvailable") }}
                        </v-chip>
                        <v-chip
                          class="bg-gray-300 p-2 mb-3 w-100 text-center justify-content-center"
                        >
                          {{
                            $t("Orders.packageDurationDialog", {
                              time: pack.duration,
                            })
                          }}
                        </v-chip>
                        <base-picker-input
                          class="w-100"
                          type="time"
                          :placeholder="$t('Orders.newTime')"
                          v-model="pack.newTime"
                          :allowed-times="allowedTimes"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="salonType == 'freelancer'"
                    class="d-flex flex-wrap justify-content-center text-center gap-4 w-100"
                  >
                    <!-- Services -->
                    <div
                      class="d-flex flex-row flex-wrap justify-content-center"
                      v-if="
                        services.length > 0 && workingDays.length > 0 && newDate
                      "
                    >
                      <div
                        v-for="service in services"
                        :key="service.id"
                        class="d-flex flex-column align-items-center justify-content-center m-3 mt-0 p-4 border rounded"
                        style="min-width: 200px"
                      >
                        <v-chip
                          class="bg-gray-300 p-2 mb-2 w-100 text-center justify-content-center"
                        >
                          {{ service.name }}
                        </v-chip>
                        <!-- <v-chip
                          class="bg-gray-300 p-2 mb-3 w-100 text-center justify-content-center"
                        >
                          {{ service.time || $t("Orders.noTimesAvailable") }}
                        </v-chip> -->
                        <v-chip
                          class="bg-gray-300 p-2 mb-3 w-100 text-center justify-content-center"
                        >
                          {{
                            $t("Orders.serviceDurationDialog", {
                              time: service.duration,
                            })
                          }}
                        </v-chip>
                      </div>
                    </div>

                    <!-- Packages -->
                    <div
                      class="d-flex flex-row flex-wrap justify-content-center"
                      v-if="
                        packages.length > 0 && workingDays.length > 0 && newDate
                      "
                    >
                      <div
                        v-for="pack in packages"
                        :key="pack.id"
                        class="d-flex flex-column align-items-center justify-content-center m-3 mt-0 p-4 border rounded"
                        style="min-width: 200px"
                      >
                        <v-chip
                          class="bg-gray-300 p-2 mb-2 w-100 text-center justify-content-center"
                        >
                          {{ pack.name }}
                        </v-chip>
                        <!-- <v-chip
                          class="bg-gray-300 p-2 mb-3 w-100 text-center justify-content-center"
                        >
                          {{ pack.time || $t("Orders.noTimesAvailable") }}
                        </v-chip> -->
                        <v-chip
                          class="bg-gray-300 p-2 mb-3 w-100 text-center justify-content-center"
                        >
                          {{
                            $t("Orders.packageDurationDialog", {
                              time: pack.duration,
                            })
                          }}
                        </v-chip>
                      </div>
                    </div>
                    <!-- New Time Picker for Freelancer -->
                    <hr class="border-0 w-100 m-0" />
                    <base-picker-input
                      v-if="
                        services.length > 0 && workingDays.length > 0 && newDate
                      "
                      class="w-50 mt-0"
                      type="time"
                      :placeholder="$t('Orders.newTime')"
                      v-model="freelanceNewTime"
                      :allowed-times="allowedTimes"
                    />
                  </div>
                </div>
              </form>

              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="validateDateChange">
                  {{ $t("BUTTONS.ok") }}
                </v-btn>

                <v-btn
                  class="modal_cancel_btn"
                  @click="dialogDateChangeClose"
                  >{{ $t("BUTTONS.cancel") }}</v-btn
                >
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Deactivate Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

export default {
  name: "AllRegions",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
    activeStatuses() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.new"),
          value: "new",
        },
        {
          id: 2,
          name: this.$t("STATUS.accepted"),
          value: "accepted",
        },
        {
          id: 3,
          name: this.$t("STATUS.rejected"),
          value: "rejected",
        },
      ];
    },
    userType() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.moder"),
          value: "manager",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.impular"),
          value: "employee",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      isWaitingRequestPrice: false,
      chosenOrder: null,
      switchOpen: false,
      orderData: null,
      addOpen: false,
      addData: false,
      serviceDesc: null,
      servicePrice: null,
      pdf: null,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        deliverName: null,

        storeName: null,
        orderNum: null,
        mobileClient: null,
        clientName: null,
        status: null,
        mangement: null,
      },
      allProviders: [],
      allStatus: [],
      allRanks: [],
      salonType: null,

      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Clients.serialNumber"),
          value: "serial_number",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderNum"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.clientName"),
          value: "client_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.clientPhone"),
          value: "client_mobile",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.provider_name"),
          value: "salon.name",
          sortable: false,
          align: "center",
        },
        // {
        //   text: this.$t("TABLES.Orders.deliveryName"),
        //   value: "driver.name",
        //   sortable: false,
        //   align: "center",
        // },
        {
          text: this.$t("TABLES.subscriptions.Totalorder"),
          value: "total_after_discount",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.cancelStatus"),
          value: "cancel_status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      today: new Date(),
      // End:: Table Data
      workingDays: [
        {
          id: null,
          value: null, // This will be used for v-model
          label: null, // This will be displayed in the dropdown
        },
      ],
      allowedTimes: [],
      salon_id: null,
      newDate: null,
      newTime: null,
      orderDate: null,
      orderHour: null,

      services: [
        {
          id: null,
          name: null,
          time: null,
          newTime: null,
        },
      ],
      packages: [
        {
          id: null,
          name: null,
          time: null,
          newTime: null,
        },
      ],
      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data

      permissions: [],

      status_now: null,

      // Start:: Dialogs Control Data
      dialogReplay: false,
      selectedReplayTextToShow: "",
      dialogImage: false,
      selectedItemImage: null,
      dialogDateChange: false,
      itemToChangeDate: null,
      deactivateReason: null,
      dialogDelete: false,
      dialogAccept: false,
      itemToAccept: null,
      provider_cancel_discount: null,
      client_cancel_composition: null,
      // End:: Dialogs Control Data
      status_now: null,

      // balance_package: null
      number: null,
      users: [],
      mangement: [],
      SimlerOrder: [],
      number: null,
      freelanceNewTime: null,
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
    newDate: async function (date) {
      const getDayName = (date, locale = "en-US") => {
        const x = new Date(date);
        return x.toLocaleDateString(locale, { weekday: "long" });
      };

      const theDay = getDayName(date);
      console.log("Selected day:", theDay);

      await this.getWorkingDays(theDay); // Wait for workingDays to update

      // Optional: now you can safely call generateAllowedTimes()
      this.allowedTimes = this.generateAllowedTimes();
      console.log("Allowed times after update:", this.allowedTimes);
    },
  },

  methods: {
    generateAllowedTimes() {
      const allowedTimes = [];

      this.workingDays?.forEach((period) => {
        if (!period.start || !period.end) return;

        const start = this.parseTime(period.start);
        const end = this.parseTime(period.end);

        let current = new Date(start.getTime());

        while (current <= end) {
          const timeStr = current.toTimeString().split(" ")[0].substring(0, 5);
          allowedTimes.push(timeStr);
          current.setMinutes(current.getMinutes() + 5);
        }
      });

      return allowedTimes;
    },

    parseTime(str) {
      const [hours, minutes] = str.split(":");
      const date = new Date();
      date.setHours(+hours, +minutes, 0, 0);
      return date;
    },
    disabledDate(current) {
      return current && current < moment().startOf("day");
    },
    async getWorkingDays(theDay) {
      this.loading = false;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "working-days?ignorePermissionCheck=1",
          params: {
            page: this.paginations.current_page,
            salon_id: this.salon_id,
          },
        });
        console.log("getWorkingDays List", res);

        // Filter the response by the selected day name
        const matchedDay = res.data.data.data.find(
          (day) => day.day === theDay && day.working_periods.length > 0
        );

        this.workingDays = matchedDay
          ? matchedDay.working_periods.map((p, i) => ({
              id: i,
              value: `${this.$t("PLACEHOLDERS.from")} ${p.start_time} ${this.$t(
                "PLACEHOLDERS.to"
              )} ${p.end_time}`,
              start: p.start_time,
              end: p.end_time,
            }))
          : [];

        console.log("period Times", this.workingDays);

        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
      }
    },
    // End:: Set Table Rows
    // Start:: Get Data To Edit
    async getDataToEdit(id) {
      this.loading = false;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `orders/${id}`,
        });
        console.log("order details", res);

        // Start:: Set Data
        this.salon_id = res.data.data.data?.salon?.id;
        this.orderDate = res.data.data.data?.date;
        this.orderHour = res.data.data.data?.start_time;
        this.salonType = res.data.data.data?.salon?.type;

        console.log("orderDate", this.orderDate);

        // services and packages
        this.services = res.data.data.data?.services?.map((service) => {
          return {
            id: service.id,
            name: service.name,
            time: service.start_time,
          };
        });
        this.packages = res.data.data.data?.packages?.map((pack) => {
          return {
            id: pack.id,
            name: pack.name,
            time: pack.start_time,
          };
        });
        // End:: Set Data
      } catch (error) {
        console.log(error.response.data.message);
      }
    },
    async selectDateChangeItem(item) {
      this.dialogDateChange = true;
      console.log("Date Change", item);
      await this.getDataToEdit(item.id);
      this.itemToChangeDate = item;
    },
    dialogDateChangeClose() {
      this.setTableRows();
      this.itemToChangeDate = null;
      this.newDate = null;
      this.newTime = null;
      this.freelanceNewTime = null;
      for (const service of this.services) {
        service.newTime = null;
      }
      this.allowedTimes = null;
      for (const pack of this.packages) {
        pack.newTime = null;
      }
      this.allowedTimes = null;
      this.dialogDateChange = false;
    },
    validateDateChange() {
      if (this.salonType == "salon") {
        if (!this.newDate) {
          this.$message.error(this.$t("MESSAGES.newDateRequired"));
          return;
        }

        for (const service of this.services) {
          if (!service.newTime) {
            this.$message.error(
              this.$t("MESSAGES.newTimeRequired", { name: service.name })
            );
            return;
          }
        }

        for (const pack of this.packages) {
          if (!pack.newTime) {
            this.$message.error(
              this.$t("MESSAGES.newTimeRequired", { name: pack.name })
            );
            return;
          }
        }
      }
      if (this.salonType == "freelance") {
        if (!this.newDate) {
          this.$message.error(this.$t("MESSAGES.newDateRequired"));
          return;
        }
        if (!this.freelanceNewTime) {
          this.$message.error(this.$t("MESSAGES.newTimeRequiredAll"));
          return;
        }
      }
      this.HandlingChangeDate();
    },
    async HandlingChangeDate() {
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("date", this.newDate);
      if (this.salonType == "salon") {
        if (this.services.length > 0) {
          this.services.forEach((service, index) => {
            REQUEST_DATA.append(`services[${index}][id]`, service.id);
            REQUEST_DATA.append(
              `services[${index}][start_time]`,
              service.newTime
            );
          });
        }
        this.packages.forEach((pack, index) => {
          REQUEST_DATA.append(`packages[${index}][id]`, pack.id);
          REQUEST_DATA.append(`packages[${index}][start_time]`, pack.newTime);
        });
      }

      if (this.salonType == "freelancer" && this.freelanceNewTime) {
        REQUEST_DATA.append("time", this.freelanceNewTime);
      }

      try {
        await this.$axios({
          method: "POST",
          url: `/orders/change-time/${this.itemToChangeDate.id}`,
          data: REQUEST_DATA,
        });
        this.$message.success(this.$t("MESSAGES.changeDateSuccessfully"));
        this.setTableRows();
        this.itemToChangeDate = null;
        this.newDate = null;
        this.newTime = null;
        this.freelanceNewTime = null;
        this.allowedTimes = null;
        this.dialogDateChange = false;
      } catch (error) {
        this.$message.error(error.response.data.message);
        this.dialogDateChange = true;
      }
    },
    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/request_cancellation/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.orderNum = null;
      this.filterOptions.storeName = null;
      this.filterOptions.clientName = null;
      this.filterOptions.status = null;
      // this.filterOptions.deliverName = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/request_cancellation/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "orders/request_cancellation",
          params: {
            page: this.paginations.current_page,
            id: this.filterOptions.orderNum,
            clientName: this.filterOptions.clientName,
            salonName: this.filterOptions.storeName,
            cancelStatus: this.filterOptions.status?.value,
          },
        });
        console.log("request_cancellation", res);
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },
    // End:: Set Table Rows

    // ===== Start:: Handling Activation & Deactivation
    selectAcceptItem(item) {
      this.dialogAccept = true;
      this.itemToAccept = item;
      console.log("itemToAccept", this.itemToAccept);
    },
    async confirmAcceptItem() {
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append(
        "provider_cancel_discount",
        this.provider_cancel_discount ? this.provider_cancel_discount : 0
      );
      REQUEST_DATA.append(
        "client_cancel_composition",
        this.client_cancel_composition ? this.client_cancel_composition : 0
      );

      try {
        await this.$axios({
          method: "POST",
          url: `orders/accept-request-cancellation/${this.itemToAccept.id}`,
          data: REQUEST_DATA,
        });
        this.dialogAccept = false;

        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.verifiedSuccessfully"));
        this.provider_cancel_discount = null;
        this.client_cancel_composition = null;
      } catch (error) {
        this.$message.error(error.response.data.message);
      }
    },
    async HandlingItemActivationStatus(item) {
      const REQUEST_DATA = new FormData();
      try {
        await this.$axios({
          method: "POST",
          url: `orders/reject-request-cancellation/${item.id}`,
          // data: REQUEST_DATA,
        });
        this.$message.success(this.$t("MESSAGES.reject_request"));
        this.setTableRows();
      } catch (error) {
        this.$message.error(error.response.data.message);
      }
    },

    // ===== End:: Handling Activation & Deactivation

    // ==================== Start:: Crud ====================
    // ===== Start:: Show
    showItem(item) {
      this.$router.push({ path: `/request_cancellation/show/${item.id}` });
    },
    // ===== End:: Show
    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }

    this.setTableRows();
    // this.getUsers();
    // this.getManeg();
    // End:: Fire Methods
  },
};
</script>
<style>
span.submit_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  transition: all 0.3s linear;
  background-color: #f6a513;
  display: flex;
  justify-content: center;
  align-items: center;
}
.dash {
  cursor: not-allowed !important;
  pointer-events: none !important;
}
.ant-time-picker-panel-select ul {
  padding: 0px 0px 0px !important;
}
</style>
<style scoped>
.v-application .my-5 {
  margin-top: 10px !important;
  margin-bottom: 0px !important;
}
</style>
